// Interview Session detail page — focused view of one upcoming interview
// with three side-by-side tabs: Prep pack · Practice · Contacts.
//
// Replaces the inline-accordion pattern in the old Interview.tsx where each
// session card expanded a sub-tab inline. The user wanted "click directly
// into the function" without nested expansion, so each entry-point button on
// the list page nav's here with ?tab=prep|practice|contacts.
//
// Deep research is intentionally NOT here — it lives on ApplicationDetail's
// Intel tab (Phase 12) and on the standalone JobDetail page. Putting another
// drawer here was redundant and crowded the UI.

import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ExternalLink, Download, FileText, BookOpen, Sparkles, MessageSquare, Mic } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Chip, { stageVariant } from '../components/ui/Chip';
import PrepPanel from '../components/domain/PrepPanel';
import ContactsPanel from '../components/domain/ContactsPanel';
import PracticeForSession from '../components/domain/PracticeForSession';
import { apiGet } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import type { Application } from '../lib/types';

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  company_slug: string | null;
  company: string;
  date: string;
  mtime: number;
  bytes: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

type Tab = 'prep' | 'practice' | 'contacts';

const TAB_LABELS: Record<Tab, string> = {
  prep: 'Prep pack',
  practice: 'Practice',
  contacts: 'Contacts',
};

export default function InterviewSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: Tab = (() => {
    const t = searchParams.get('tab');
    if (t === 'practice' || t === 'contacts') return t;
    return 'prep';
  })();

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const dataQ = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });

  const session: Application | undefined = useMemo(() => {
    const apps = dataQ.data?.applications ?? [];
    return apps.find((a) => a.id === sessionId || String(a.num) === sessionId);
  }, [dataQ.data, sessionId]);

  const slug = session ? companySlug(session.company) : '';

  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    staleTime: 30_000,
  });
  const tailored = useMemo(() => {
    const items = tailoredQ.data?.items ?? [];
    return items.find((t) => t.company_slug === slug) || null;
  }, [tailoredQ.data, slug]);

  if (dataQ.isLoading) {
    return (
      <div className="page-enter">
        <div className="empty">Loading session…</div>
      </div>
    );
  }
  if (!session) {
    return (
      <div className="page-enter">
        <button className="btn ghost" style={{ marginBottom: 18 }} onClick={() => nav('/interview')}>
          <ChevronLeft size={12} style={{ marginRight: 4, display: 'inline' }} /> Interviews
        </button>
        <div className="card empty" style={{ padding: 28 }}>
          <span className="serif">Session not found.</span>
          Move an application into the Interview or Phone screen stage on the Applications page first.
        </div>
      </div>
    );
  }

  const sid = session.id || String(session.num);

  return (
    <div className="page-enter">
      <button className="btn ghost" style={{ marginBottom: 14 }} onClick={() => nav('/interview')}>
        <ChevronLeft size={12} style={{ marginRight: 4, display: 'inline' }} /> Interviews
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 6 }}>
        <LogoSquare name={session.company} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <PageHeader
            eyebrow={
              <>
                Interview · {session.company}
                <span style={{ marginLeft: 10 }}>
                  <Chip variant={stageVariant(session.stage)} style={{ fontSize: 10.5 }}>
                    {session.stage}
                  </Chip>
                </span>
                <span style={{ marginLeft: 10, color: 'var(--ink3)', fontSize: 11 }}>
                  {session.date} · fit {session.score.toFixed(1)}/5
                </span>
              </>
            }
            title={session.role}
            sub="Generate a prep pack, practice the questions out loud, draft outreach to contacts. Everything runs locally via Ollama."
            actions={
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {session.url && (
                  <a className="btn" href={session.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <ExternalLink size={12} /> Posting
                  </a>
                )}
                {tailored && (
                  <a className="btn" href={tailored.pdf_url} download={tailored.pdf_file} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} title={`${tailored.pdf_file} · ${tailored.date}`}>
                    <Download size={12} /> Tailored CV
                  </a>
                )}
                {session.report_file && (
                  <button className="btn" onClick={() => nav(`/apps/${encodeURIComponent(sid)}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <FileText size={12} /> Evaluation
                  </button>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line)', marginBottom: 22, marginTop: 6 }}>
        {(['prep', 'practice', 'contacts'] as Tab[]).map((t) => (
          <TabBtn key={t} active={tab === t} onClick={() => setTab(t)}>
            {t === 'prep' && <BookOpen size={12} />}
            {t === 'practice' && <Mic size={12} />}
            {t === 'contacts' && <MessageSquare size={12} />}
            {TAB_LABELS[t]}
          </TabBtn>
        ))}
        <div style={{ flex: 1 }} />
        {tab === 'prep' && (
          <span style={{ fontSize: 11, color: 'var(--ink3)', alignSelf: 'center', paddingRight: 8 }}>
            <Sparkles size={11} style={{ marginRight: 4, display: 'inline' }} />
            Generate a position-specific question bank
          </span>
        )}
      </div>

      {tab === 'prep' && <PrepPanel session={session} sessionId={sid} />}
      {tab === 'practice' && (
        <PracticeForSession
          session={session}
          sessionId={sid}
          onSwitchToPrepTab={() => setTab('prep')}
        />
      )}
      {tab === 'contacts' && <ContactsPanel session={session} sessionId={sid} />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      className="btn ghost"
      onClick={onClick}
      style={{
        border: 'none',
        borderRadius: 0,
        padding: '10px 16px',
        borderBottom: active ? '2px solid var(--pri)' : '2px solid transparent',
        color: active ? 'var(--ink)' : 'var(--ink3)',
        fontWeight: active ? 600 : 400,
        fontSize: 13,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}
